import { useState, useEffect } from 'react';
import { supabase } from '@/lib/customSupabaseClient';

const ITEMS_PER_PAGE = 50;

export function useSimpleExerciseSearch() {
  const [searchTerm, setSearchTerm] = useState('');
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [refetchCount, setRefetchCount] = useState(0);

  // Reset page to 0 whenever search term changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  useEffect(() => {
    const fetchExercises = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch one extra item to determine if there is a next page
        const { data, error: supabaseError } = await supabase
          .rpc('admin_search_exercises_simple', {
            p_search_term: searchTerm || null,
            p_limit: ITEMS_PER_PAGE + 1,
            p_offset: page * ITEMS_PER_PAGE
          });

        if (supabaseError) throw supabaseError;

        const dataList = data || [];
        const hasNextPage = dataList.length > ITEMS_PER_PAGE;
        
        setHasMore(hasNextPage);
        // Only return the requested amount (limit) to the UI
        setExercises(hasNextPage ? dataList.slice(0, ITEMS_PER_PAGE) : dataList);
      } catch (err) {
        console.error('Error searching exercises:', err);
        setError(err.message);
        setExercises([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchExercises();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, page, refetchCount]);

  const nextPage = () => {
    if (hasMore) setPage(prev => prev + 1);
  };

  const previousPage = () => {
    if (page > 0) setPage(prev => prev - 1);
  };

  const refetch = () => setRefetchCount(c => c + 1);

  return {
    searchTerm,
    setSearchTerm,
    exercises,
    loading,
    error,
    page,
    nextPage,
    previousPage,
    hasMore,
    hasPreviousPage: page > 0,
    refetch,
  };
}